
import { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, onSnapshot, getDoc } from "firebase/firestore";

/**
 * Live-load community doc and expand its members[] UIDs into resident user docs.
 * Returns: { community, residents, loading, error }
 */
export default function useCommunityResidents(communityId) {
  const [community, setCommunity] = useState(null);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resLoading, setResLoading] = useState(false);
  const [error, setError] = useState("");

  // Live community
  useEffect(() => {
    if (!communityId) {
      setCommunity(null);
      setLoading(false);
      setError("Missing communityId");
      return;
    }
    setLoading(true);
    setError("");
    const ref = doc(db, "communities", communityId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setCommunity(null);
          setError("Community not found.");
        } else {
          setCommunity({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      },
      (err) => {
        console.error("[useCommunityResidents] community error:", err);
        setError(err?.message || "Failed to load community.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [communityId]);

  // Expand members -> users
  useEffect(() => {
    const uids = Array.isArray(community?.members) ? community.members : [];
    if (!uids.length) {
      setResidents([]);
      return;
    }
    let cancelled = false;
    setResLoading(true);
    Promise.all(uids.map((uid) => getDoc(doc(db, "users", uid))))
      .then((snaps) => {
        if (cancelled) return;
        const rows = snaps
          .filter((s) => s.exists())
          .map((s) => ({ uid: s.id, ...s.data() }));
        setResidents(rows);
      })
      .catch((err) => {
        console.error("[useCommunityResidents] residents load error:", err);
        setError(err?.message || "Failed to load residents.");
      })
      .finally(() => setResLoading(false));

    return () => {
      cancelled = true;
    };
  }, [community?.members]);

  return {
    community,
    residents,
    loading: loading || resLoading,
    error,
  };
}
